import {
    useCallback,
    useState,
    type FC
} from "react";
import {
    type LayoutChangeEvent,
    type ImageLoadEvent,
    type ImageProps,
    Animated,
    Platform,
    Image,
    View
} from "react-native";
import type IImageProps from "./type";
import {
    useStyles
} from "./stylesheet";
import {
    useAnimatedState
} from "../../../../helpers/motion";
import type {
    MarkdownImageProbeConstantType,
    MarkdownImageLoadEventType,
    MarkdownNaturalSizeType
} from "../../type";
import {
    getMarkdownImageSize
} from "../../stylesheet";
import {
    getMarkdownSafeUrl
} from "../../util";
import stylesheet from "../../stylesheet";
import Text from "../../../text";

const getMarkdownImageNaturalSize = (nativeEvent: MarkdownImageLoadEventType): MarkdownNaturalSizeType | null => {
    const width = nativeEvent.source?.width || nativeEvent.target?.naturalWidth || nativeEvent.width || 0;

    const height = nativeEvent.source?.height || nativeEvent.target?.naturalHeight || nativeEvent.height || 0;

    if(!width || !height) {
        return null;
    }

    return {
        height,
        width
    };
};

const handleMarkdownImageProbe = ({
    onLoadSize,
    uri
}: MarkdownImageProbeConstantType) => {
    if(Platform.OS !== "web") {
        Image.getSize(uri, (width, height) => onLoadSize({
            height,
            width
        }), () => undefined);

        return;
    }

    const probe = new globalThis.Image();

    probe.onload = () => onLoadSize({
        height: probe.naturalHeight,
        width: probe.naturalWidth
    });

    probe.src = uri;
};

const getMarkdownImageCached = (uri?: string): boolean => {
    if(!uri || Platform.OS !== "web" || typeof globalThis.Image !== "function") {
        return false;
    }

    const probe = new globalThis.Image();

    probe.src = uri;

    return probe.complete && probe.naturalWidth > 0;
};

const MarkdownImage: FC<IImageProps> = ({
    isShowCaption,
    shapeConfigs,
    customTheme,
    imageProps,
    radiuses,
    colors,
    spaces,
    node
}) => {
    const safeUrl = getMarkdownSafeUrl({
        url: node.url,
        isImage: true
    });

    const [
        availableWidth,
        setAvailableWidth
    ] = useState<number | null>(null);

    const [
        naturalSize,
        setNaturalSize
    ] = useState<MarkdownNaturalSizeType | null>(null);

    const [
        loadedUrl,
        setLoadedUrl
    ] = useState<string | undefined>(() => getMarkdownImageCached(safeUrl) ? safeUrl : undefined);

    const currentImageProps: ImageProps = imageProps ?? {};

    const {
        style: imageStyleProp,
        onLoad: onLoadProp,
        ...imageRestProps
    } = currentImageProps;

    const currentSize = getMarkdownImageSize({
        size: node.size,
        availableWidth,
        naturalSize
    });

    const {
        figure: figureDynamicStyle,
        image: imageDynamicStyle
    } = useStyles({
        shapeConfigs,
        currentSize,
        radiuses,
        colors,
        spaces
    });

    const opacity = useAnimatedState({
        value: loadedUrl === safeUrl ? 1 : 0,
        intent: "surfaceEnter",
        motion: "fade",
        customTheme
    });

    const onLoad = useCallback((event: ImageLoadEvent) => {
        const handleNaturalSize = (loadedSize: MarkdownNaturalSizeType) => {
            if(!loadedSize.width || !loadedSize.height) {
                return;
            }

            setNaturalSize((currentNaturalSize) => {
                if(currentNaturalSize && currentNaturalSize.width === loadedSize.width && currentNaturalSize.height === loadedSize.height) {
                    return currentNaturalSize;
                }

                return loadedSize;
            });
        };

        setLoadedUrl(safeUrl);

        const loadedSize = getMarkdownImageNaturalSize(event.nativeEvent);

        if(loadedSize) {
            handleNaturalSize(loadedSize);
        } else if(safeUrl) {
            handleMarkdownImageProbe({
                onLoadSize: handleNaturalSize,
                uri: safeUrl
            });
        }

        onLoadProp?.(event);
    }, [
        onLoadProp,
        safeUrl
    ]);

    const onFigureLayout = (event: LayoutChangeEvent) => {
        setAvailableWidth(event.nativeEvent.layout.width);
    };

    if(!safeUrl) {
        return null;
    }

    const alt = node.content;

    const caption = node.title || alt;

    const renderCaption = () => {
        if(!isShowCaption || !caption) {
            return null;
        }

        return <Text
            style={stylesheet.imageCaption}
            aria-hidden={caption === alt}
            customTheme={customTheme}
            variant="bodySmallSize"
            color="low"
        >
            {caption}
        </Text>;
    };

    return <View
        role={Platform.OS === "web" ? "figure" : undefined}
        onLayout={onFigureLayout}
        style={[
            stylesheet.figure,
            figureDynamicStyle
        ]}
    >
        <Animated.Image
            fadeDuration={0}
            {...imageRestProps}
            resizeMode={node.resizeMode ? node.resizeMode : "contain"}
            accessibilityLabel={alt || undefined}
            role={alt ? "img" : undefined}
            style={[
                stylesheet.image,
                imageDynamicStyle,
                {
                    opacity
                },
                imageStyleProp
            ]}
            aria-hidden={!alt}
            accessible={!!alt}
            source={{
                uri: safeUrl
            }}
            onLoad={onLoad}
            alt={alt}
        />
        {renderCaption()}
    </View>;
};
export default MarkdownImage;
